import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import auditService, { type UploadReceiptResponse } from '../../../../services/auditService';
import authService, { type ActiveGamesAndPacksResponse, type StoreLocation } from '../../../../services/authService';
import { getAuditReportExpanded, type ExpandedAuditReport } from '../../../../services/storeAuditReportsService';
import { useGamesData } from '../../../../hooks/useGamesData';
import { usePreviousAuditData } from '../../../../hooks/usePreviousAuditData';
import {
  fetchGamesAndUpdateData,
  processTicketDetailsWithPrevious,
} from '../../../../utils/fixOcrDataProcessors';
import { parseShiftDateAndTime, getDateAndTimeFromDetails } from '../../../../utils/fixOcrDateUtils';
import { convertFormToApiData } from '../../../../utils/fixOcrMappers';
import showToast from '../../../../utils/toastUtils';
import {
  buildFixOcrUpdateRequest,
  validateFixOcrFormState,
} from '../utils/fixOcrPageUtils';
import { buildNonTicketReportUpdateRequest } from '../utils/nonTicketReportPageUtils';
import {
  resolveNonTicketReportDate,
  resolveNonTicketReportTime,
  resolveVendingInventoryReportDate,
  resolveVendingInventoryReportTime,
  resolveVendingSalesReportDate,
} from '../utils/nonTicketReportDateUtils';
import { FIX_OCR_STORE_ID_PARAM, FIX_OCR_STORE_ID_PARAM_LEGACY } from '../utils/fixOcrQueryParams';
import type { ReceiptAuditSimulationResponse } from '../../../../services/auditService';
import type { AuditVarianceByGameId } from '../../../../types/auditVarianceTypes';
import { buildAuditVarianceByGameId } from '../utils/receiptAuditVarianceUtils';
import { compareVendingInventoryPacks } from '../utils/vendingInventoryComparisonUtils';
import { resolveVendingLocationConfig } from '../utils/vendingLocationUtils';
import type {
  VendingInventoryMappingContextResponse,
  VendingInventoryPreviousDateResponse,
} from '../../../../types/services/audit';

const resolveLotteryTerminalId = (finalJson: any, details: any): number | string | null => {
  if (finalJson?.terminal !== null && finalJson?.terminal !== undefined && finalJson?.terminal !== '') {
    return finalJson.terminal;
  }

  const ocrSources = [
    details?.parsed_data?.ocr_text,
    ...(Array.isArray(details?.parsed_data?.ocr_runs)
      ? details.parsed_data.ocr_runs.map((run: any) => run?.text)
      : []),
  ];

  for (const source of ocrSources) {
    if (typeof source !== 'string') {
      continue;
    }
    const match = source.match(/TERMINAL\D{0,20}(\d{6,12})/i);
    if (match) {
      return match[1];
    }
  }

  return null;
};

const resolveLocationKeyFromDetails = (details: any, finalJson: any): string => {
  const parsedData = details?.parsed_data ?? {};
  const parsedFinalJson = parsedData?.final_json ?? {};
  const candidates = [
    parsedData?.location_key,
    parsedData?.loaction_key,
    parsedFinalJson?.location_key,
    parsedFinalJson?.loaction_key,
    finalJson?.location_key,
    finalJson?.loaction_key,
  ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined) {
      const normalized = String(candidate).trim();
      if (normalized) {
        return normalized;
      }
    }
  }

  return '';
};

const buildVendingInventoryLineKey = (bin: unknown, gameId: unknown): string => {
  const binKey = bin === null || bin === undefined || bin === '' ? '' : String(bin).trim();
  const gameKey = gameId === null || gameId === undefined || gameId === '' ? '' : String(gameId).trim();
  return `${binKey}:${gameKey}`;
};

const applyVendingInventoryPackMapping = (
  candidateLines: unknown,
  mappingContext: VendingInventoryMappingContextResponse | null,
) => {
  const lines = Array.isArray(candidateLines) ? candidateLines : [];
  if (!mappingContext || !Array.isArray(mappingContext.lines) || mappingContext.lines.length === 0) {
    return lines;
  }

  const mappingByLineKey = new Map<string, {
    pack: string;
    candidates: Array<{
      pack_id?: number | null;
      pack_location?: string | null;
      status?: string | null;
      status_date?: string | null;
      activated_date?: string | null;
      settled_date?: string | null;
      backstock_state?: string | null;
      last_backstock_location?: string | null;
      latest_remaining_qty?: number | null;
    }>;
    requiredPackCount: number | null;
    packCapacity: number | null;
    selectedPacks: Array<{
      pack_id?: number | null;
      remaining_qty?: number | null;
    }>;
  }>();
  mappingContext.lines.forEach((line) => {
    const key = buildVendingInventoryLineKey(line?.bin, line?.game_id);
    if (!key || mappingByLineKey.has(key)) {
      return;
    }

    const selectedPackId = line?.selected_pack_id;
    const pack = selectedPackId === null || selectedPackId === undefined
      ? '-'
      : String(selectedPackId);
    const selectedPacks = Array.isArray(line?.selected_packs)
      ? line.selected_packs.map((packEntry) => ({
          pack_id: packEntry?.pack_id ?? null,
          remaining_qty: packEntry?.remaining_qty ?? null,
        }))
      : [];
    const candidates = Array.isArray(line?.candidates)
      ? line.candidates.map((candidate) => ({
          pack_id: candidate?.pack_id ?? null,
          pack_location: candidate?.pack_location ?? null,
          status: candidate?.status ?? null,
          status_date: candidate?.status_date ?? null,
          activated_date: candidate?.activated_date ?? null,
          settled_date: candidate?.settled_date ?? null,
          backstock_state: candidate?.backstock_state ?? null,
          last_backstock_location: candidate?.last_backstock_location ?? null,
          latest_remaining_qty: candidate?.latest_remaining_qty ?? null,
        }))
      : [];

    mappingByLineKey.set(key, {
      pack,
      candidates,
      requiredPackCount: line?.required_pack_count ?? null,
      packCapacity: line?.pack_capacity ?? null,
      selectedPacks,
    });
  });

  return lines.map((line: any) => {
    const key = buildVendingInventoryLineKey(line?.bin, line?.game);
    const mapping = mappingByLineKey.get(key);
    if (!mapping) {
      return line;
    }

    return {
      ...line,
      pack: mapping.pack,
      selected_pack_id: mapping.selectedPacks.find((packEntry) => packEntry?.pack_id !== null && packEntry?.pack_id !== undefined)?.pack_id ?? line?.selected_pack_id ?? null,
      required_pack_count: mapping.requiredPackCount,
      pack_capacity: mapping.packCapacity,
      selected_packs: mapping.selectedPacks,
      pack_candidates: mapping.candidates,
    };
  });
};

const getCurrentVendingInventoryLines = (reportType: string, data: any): unknown[] => {
  const finalJson = data?.parsed_data?.final_json ?? {};
  if (reportType === 'vending_shift_report') {
    return Array.isArray(finalJson?.inventory?.lines) ? finalJson.inventory.lines : [];
  }

  return Array.isArray(finalJson?.lines) ? finalJson.lines : [];
};

export const useFixOcrPageLogic = () => {
  const { queueId } = useParams<{ queueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [reportType, setReportType] = useState('ticket_balance');
  const [storeName, setStoreName] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState<'reject' | 'revert' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>(null);
  const [apiData, setApiData] = useState<any>(null);
  const [editMode, setEditMode] = useState<'form' | 'json' | 'enhanced'>('form');
  const [auditStatus, setAuditStatus] = useState<string>('');
  const [editReceiptDialogOpen, setEditReceiptDialogOpen] = useState(false);
  const [auditReportDialogOpen, setAuditReportDialogOpen] = useState(false);
  const [auditReport, setAuditReport] = useState<ExpandedAuditReport | null>(null);
  const [auditReportLoading, setAuditReportLoading] = useState(false);
  const [revertingAudit, setRevertingAudit] = useState(false);
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any | null>(null);
  const [rejectNotesDialogOpen, setRejectNotesDialogOpen] = useState(false);
  const [rejectNotesSaving, setRejectNotesSaving] = useState(false);
  const [pendingInvalidGames, setPendingInvalidGames] = useState<Array<{ game: string | number; pack?: string | number; range?: string }>>([]);
  const [pendingInvalidPacks, setPendingInvalidPacks] = useState<Array<{ game: string | number; pack: string | number; range?: string }>>([]);
  const [validatingGamesPacks, setValidatingGamesPacks] = useState(false);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [simulationResult, setSimulationResult] = useState<ReceiptAuditSimulationResponse | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [activeGamesAndPacks, setActiveGamesAndPacks] = useState<ActiveGamesAndPacksResponse>([]);
  const [previousVendingInventory, setPreviousVendingInventory] = useState<VendingInventoryPreviousDateResponse | null>(null);
  const [storeLocations, setStoreLocations] = useState<StoreLocation[]>([]);

  const formRef = useRef<any>(null);
  const loadingRef = useRef(false);
  const loadedQueueIdRef = useRef<string | null>(null);
  const lastValidatedFormStateKeyRef = useRef('');
  const simulationRequestKeyRef = useRef('');
  const initialSimulationQueueIdRef = useRef<string | null>(null);

  const { loadPreviousAuditData } = usePreviousAuditData();
  const { fetchGamesDataOnly, getGamesMap, gamesMapState, gamesData } = useGamesData();

  const isTicketBalanceReport = reportType === 'ticket_balance';
  const isVendingInventoryReport = reportType === 'vending_inventory' || reportType === 'vending_shift_report';

  // Used to force remount of RHF forms when the loaded report changes.
  // This avoids stale resolver/schema context when switching between report types (e.g. vending_sales -> vending_inventory).
  const formKey = useMemo(() => `${queueId ?? 'no-queue'}-${reportType}`, [queueId, reportType]);

  const storeId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(FIX_OCR_STORE_ID_PARAM) || params.get(FIX_OCR_STORE_ID_PARAM_LEGACY) || '';
  }, [location.search]);

  const storeIdForReceipt = useMemo(
    () => (apiData?.store_id || storeId) as string,
    [apiData?.store_id, storeId],
  );

  const varianceByGameId: AuditVarianceByGameId = useMemo(
    () => buildAuditVarianceByGameId(simulationResult?.expanded_preview ?? null),
    [simulationResult?.expanded_preview],
  );

  const vendingInventoryComparison = useMemo(() => {
    if (!isVendingInventoryReport) {
      return { newPackKeys: [], completedPacks: [] };
    }

    const previousLines = previousVendingInventory?.lines ?? [];
    const currentLines = getCurrentVendingInventoryLines(reportType, formData);
    return compareVendingInventoryPacks(previousLines, currentLines);
  }, [formData, isVendingInventoryReport, previousVendingInventory?.lines, reportType]);

  useEffect(() => {
    if (!queueId || !isVendingInventoryReport) {
      setPreviousVendingInventory(null);
      return;
    }

    let canceled = false;
    auditService.getVendingInventoryPreviousDate(queueId)
      .then((response) => {
        if (canceled) return;
        setPreviousVendingInventory(response);
      })
      .catch(() => {
        if (canceled) return;
        setPreviousVendingInventory(null);
      });

    return () => {
      canceled = true;
    };
  }, [isVendingInventoryReport, queueId]);

  const runSimulation = useCallback((parsedData: any) => {
    if (!isTicketBalanceReport) {
      return;
    }

    if (!queueId) {
      setSimulationError('No queue ID provided');
      return;
    }

    const requestKey = JSON.stringify(parsedData ?? {});
    simulationRequestKeyRef.current = requestKey;
    setSimulationLoading(true);
    setSimulationError(null);

    auditService
      .simulateReceiptAudit(queueId, { parsed_data: parsedData, include_expanded_preview: true })
      .then((response) => {
        if (simulationRequestKeyRef.current !== requestKey) return;
        setSimulationResult(response);
      })
      .catch((simulationError: any) => {
        if (simulationRequestKeyRef.current !== requestKey) return;
        const errorMessage = simulationError?.message || 'Unknown error';
        setSimulationError(errorMessage);
      })
      .finally(() => {
        if (simulationRequestKeyRef.current !== requestKey) return;
        setSimulationLoading(false);
      });
  }, [isTicketBalanceReport, queueId]);

  const handleRefreshSimulation = useCallback(() => {
    if (!isTicketBalanceReport) {
      return;
    }

    const currentValues = formRef.current?.getValues?.() ?? formData;
    if (!currentValues) {
      setSimulationError('No form data available for simulation');
      return;
    }

    const parsedData = buildFixOcrUpdateRequest(convertFormToApiData(currentValues)).parsed_data;
    runSimulation(parsedData);
  }, [formData, isTicketBalanceReport, runSimulation]);

  const commitValidationState = useCallback((validationState: ReturnType<typeof validateFixOcrFormState>) => {
    lastValidatedFormStateKeyRef.current = validationState.stateKey;
    setPendingInvalidGames(validationState.invalidGames);
    setPendingInvalidPacks(validationState.invalidPacks);
    return validationState.formData;
  }, []);

  const commitPlainFormData = useCallback((nextFormData: any) => {
    setPendingInvalidGames([]);
    setPendingInvalidPacks([]);
    lastValidatedFormStateKeyRef.current = '';
    return nextFormData;
  }, []);

  const getValidationState = useCallback(
    (nextFormData: any, validationSource: ActiveGamesAndPacksResponse = activeGamesAndPacks) => {
      return validateFixOcrFormState(nextFormData, validationSource);
    },
    [activeGamesAndPacks],
  );

  const navigateToAuditList = useCallback((successMessage?: string) => {
    const params = new URLSearchParams(location.search);
    params.delete(FIX_OCR_STORE_ID_PARAM);
    params.delete(FIX_OCR_STORE_ID_PARAM_LEGACY);
    const nextSearch = params.toString();

    navigate(
      {
        pathname: '/dashboard/audit',
        search: nextSearch ? `?${nextSearch}` : '',
      },
      successMessage ? { state: { successMessage } } : undefined,
    );
  }, [location.search, navigate]);

  const handleJsonOnChange = useCallback((data: any) => {
    setApiData(data);

    if (!isTicketBalanceReport) {
      setFormData(commitPlainFormData(data));
      return;
    }

    const validationState = getValidationState(data);
    setFormData(commitValidationState(validationState));
  }, [commitPlainFormData, commitValidationState, getValidationState, isTicketBalanceReport]);

  const handleFormDataUpdate = useCallback((updatedFormData: any) => {
    setFormData((previousFormData: any) => {
      const mergedFormData = {
        ...previousFormData,
        ...updatedFormData,
      };

      if (!isTicketBalanceReport) {
        return commitPlainFormData(mergedFormData);
      }

      const validationState = getValidationState(mergedFormData);
      if (lastValidatedFormStateKeyRef.current === validationState.stateKey) {
        return previousFormData;
      }

      return commitValidationState(validationState);
    });
  }, [commitPlainFormData, commitValidationState, getValidationState, isTicketBalanceReport]);

  const loadAuditData = useCallback(async () => {
    if (!queueId) {
      setError('No queue ID provided');
      return;
    }

    if (loadingRef.current || loadedQueueIdRef.current === queueId) {
      return;
    }

    try {
      loadingRef.current = true;
      loadedQueueIdRef.current = queueId;
      setLoading(true);
      setError(null);

      const details = await auditService.getAuditDetails(queueId);
      const normalizedQueueStatus = String(details.status || '').toLowerCase();
      if (normalizedQueueStatus === 'auto_finalized') {
        // Keep the user on this page so they can view the result and optionally revert-to-ready.
        showToast.success('No discrepancies found. Audit was auto-finalized.');
      }
      let imageBlob: Blob | null = null;
      try {
        imageBlob = await auditService.getAuditImage(queueId);
      } catch (imageError: any) {
        console.error('Failed to fetch audit image:', imageError);
        showToast.warning(imageError?.message || 'Failed to load receipt image. You can still edit and submit.');
        imageBlob = null;
      }

      setStoreName(details.store_name || '');
      setAuditStatus(details.status || '');

      const finalJson = details.parsed_data?.final_json || {};
      const detectedReportType = details.parsed_data?.detected_report_type
        || finalJson.report_type
        || (details as any)?.report_type
        || 'ticket_balance';
      setReportType(detectedReportType);
      let vendingInventoryMappingContext: VendingInventoryMappingContextResponse | null = null;
      const normalizedStatus = String(details.status || '').toLowerCase();
      if ((detectedReportType === 'vending_inventory' || detectedReportType === 'vending_shift_report') && normalizedStatus === 'ready') {
        try {
          vendingInventoryMappingContext = await auditService.getVendingInventoryMappingContext(queueId);  
        } catch (mappingError) {
          console.error('Failed to fetch vending inventory mapping context:', mappingError);
          vendingInventoryMappingContext = null;
        }
      }
      let resolvedRetailerId = finalJson.retailer || '';
      let retailerFromStoreApi = false;

      const { shift_date, shift_start_time, shift_end_time } = parseShiftDateAndTime(
        finalJson.date || '',
        finalJson.time || '',
      );

      if (imageBlob) {
        const imageObjectUrl = URL.createObjectURL(imageBlob);
        setImageUrl(imageObjectUrl);
      } else {
        setImageUrl(null);
      }

      const currentStoreId = details.store_id || storeId;
      let activeGamesAndPacksForStore: ActiveGamesAndPacksResponse = [];
      let storeLocationsForStore: StoreLocation[] = [];
      let storeStateCode = '';
      const vendingSalesReportDate = resolveVendingSalesReportDate(finalJson, details);
      const vendingInventoryReportDate = resolveVendingInventoryReportDate(finalJson, details);
      const vendingInventoryReportTime = resolveVendingInventoryReportTime(finalJson, details);
      const resolvedTerminalId = resolveLotteryTerminalId(finalJson, details);

      if (currentStoreId) {
        try {
          const storeDetailsResponse = await authService.getStoreDetails(currentStoreId);
          storeStateCode = typeof (storeDetailsResponse as any)?.state === 'string'
            ? String((storeDetailsResponse as any).state).trim().toUpperCase()
            : '';
          const storeLotteryResellerId = (storeDetailsResponse as any)?.lottery_reseller_id || '';
          const storeRetailerId = (storeDetailsResponse as any)?.retailer_id || '';

          if (storeLotteryResellerId) {
            resolvedRetailerId = storeLotteryResellerId;
            retailerFromStoreApi = true;
          } else if (storeRetailerId) {
            resolvedRetailerId = storeRetailerId;
            retailerFromStoreApi = true;
          }

          try {
            activeGamesAndPacksForStore = await authService.getActiveGamesAndPacks(currentStoreId);
            setActiveGamesAndPacks(activeGamesAndPacksForStore);
          } catch (activeGamesError) {
            console.error('Failed to fetch active games and packs for OCR validation:', activeGamesError);
            setActiveGamesAndPacks([]);
          }

          if (detectedReportType === 'vending_sales' || detectedReportType === 'vending_inventory' || detectedReportType === 'vending_shift_report') {
            try {
              storeLocationsForStore = await authService.getStoreLocations(currentStoreId);
              setStoreLocations(storeLocationsForStore);
            } catch (locationsError) {
              console.error('Failed to fetch store locations:', locationsError);
              setStoreLocations([]);
            }
          } else {
            setStoreLocations([]);
          }
        } catch (storeDetailsError) {
          console.error('Failed to fetch store details:', storeDetailsError);
        }
      }

      if (!currentStoreId) {
        return;
      }

      const { dateToUse, timeToUse } = getDateAndTimeFromDetails(details, finalJson);
      const isProcessedAudit = ['finalized', 'auto_finalized', 'auto_created'].includes(String(details.status).toLowerCase());
      const locationConfig = resolveVendingLocationConfig(
        storeLocationsForStore,
        resolveLocationKeyFromDetails(details, finalJson),
        details.store_location_id,
      );
      const resolvedLocationKey = locationConfig.locationKey;
      const nonTicketReportDate = resolveNonTicketReportDate(detectedReportType, finalJson, details);
      const nonTicketReportTime = resolveNonTicketReportTime(detectedReportType, finalJson, details);
      const editableData: any = detectedReportType !== 'ticket_balance'
        ? {
            action: isProcessedAudit && detectedReportType === 'vending_sales' ? 'revert' : (isProcessedAudit ? 'reject' : 'finalize'),
            notes: details.review_notes || details.notes || '',
            confidence_score: Number(finalJson.confidence) || details.confidence_score || 0,
            shift_date: detectedReportType === 'vending_sales'
              ? (vendingSalesReportDate || details.shift_date || '')
              : (nonTicketReportDate || vendingInventoryReportDate || ''),
            shift_start_time: detectedReportType === 'vending_inventory' || detectedReportType === 'vending_shift_report'
              ? (nonTicketReportTime || vendingInventoryReportTime)
              : '',
            location_key: resolvedLocationKey,
            store_location_id: locationConfig.locationId || details.store_location_id || null,
            vending_bin_count: locationConfig.binCount,

            parsed_data: {
              ...(details.parsed_data || {}),
              vending_bin_count: locationConfig.binCount,
              final_json: {
                ...(details.parsed_data?.final_json || {}),
                vending_bin_count: locationConfig.binCount,
                ...(detectedReportType === 'vending_inventory'
                  ? {
                      lines: applyVendingInventoryPackMapping(
                        details.parsed_data?.final_json?.lines,
                        vendingInventoryMappingContext,
                      ),
                    }
                  : {}),
                ...(detectedReportType === 'vending_shift_report'
                  ? {
                      inventory: {
                        ...(details.parsed_data?.final_json?.inventory || {}),
                        lines: applyVendingInventoryPackMapping(
                          details.parsed_data?.final_json?.inventory?.lines,
                          vendingInventoryMappingContext,
                        ),
                      },
                    }
                  : {}),
                location_key: resolvedLocationKey,


              },
            },
          }
        : {
            action: isProcessedAudit ? 'reject' : 'finalize',
            notes: details.review_notes || details.notes || '',
            confidence_score: Number(finalJson.confidence) || 0,
            shift_date,
            shift_start_time,
            shift_end_time,
            ticket_details: finalJson.ticket_details || [],
            retailer_id: resolvedRetailerId,
            retailer_from_store: retailerFromStoreApi,
            store_location_id: details.store_location_id || null,
          };

      if (detectedReportType !== 'ticket_balance') {
        const gamesDataResult = await fetchGamesDataOnly(storeStateCode || undefined);
        const gamesResponse = gamesDataResult.gamesResponse || [];
        getGamesMap(gamesResponse);
        setFormData(commitPlainFormData(editableData));
        setApiData(details);
        return;
      }

      const gamesDataResult = await fetchGamesDataOnly(storeStateCode || undefined);
      const gamesResponse = gamesDataResult.gamesResponse || [];

      if (dateToUse && timeToUse && editableData.ticket_details?.length > 0 && !isProcessedAudit) {
        const previousAuditData = await loadPreviousAuditData(currentStoreId, dateToUse, timeToUse);
        if (previousAuditData?.details && gamesResponse.length > 0) {
          editableData.ticket_details = processTicketDetailsWithPrevious(
            finalJson.ticket_details || [],
            previousAuditData.details,
            gamesResponse,
          );
        }
      }

      if (editableData.ticket_details?.length > 0) {
        editableData.ticket_details = editableData.ticket_details.sort((left: any, right: any) => {
          return parseInt(left.game, 10) - parseInt(right.game, 10);
        });
      }

      const { updatedTicketDetails } = await fetchGamesAndUpdateData(
        editableData.ticket_details,
        gamesResponse,
        activeGamesAndPacksForStore,
      );

      const validationState = validateFixOcrFormState(
        {
          ...editableData,
          ticket_details: updatedTicketDetails,
        },
        activeGamesAndPacksForStore,
      );

      setFormData(commitValidationState(validationState));
      setApiData(details);
      getGamesMap(gamesResponse);
    } catch (loadError: any) {
      const errorMessage = loadError?.message || 'Unknown error';
      setError('Failed to load audit data: ' + errorMessage);
      setActiveGamesAndPacks([]);
      loadedQueueIdRef.current = null;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [commitPlainFormData, commitValidationState, fetchGamesDataOnly, getGamesMap, loadPreviousAuditData, queueId, storeId]);

  useEffect(() => {
    if (!queueId) {
      return;
    }

    if (loadedQueueIdRef.current !== queueId) {
      loadedQueueIdRef.current = null;
      loadingRef.current = false;
    }

    if (!loadingRef.current && loadedQueueIdRef.current !== queueId) {
      void loadAuditData();
    }
  }, [loadAuditData, queueId]);

  useEffect(() => {
    if (!queueId || !formData || !isTicketBalanceReport) {
      return;
    }

    if (initialSimulationQueueIdRef.current === queueId) {
      return;
    }

    initialSimulationQueueIdRef.current = queueId;
    const parsedData = buildFixOcrUpdateRequest(convertFormToApiData(formData)).parsed_data;
    runSimulation(parsedData);
  }, [formData, isTicketBalanceReport, queueId, runSimulation]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      loadingRef.current = false;
    };
  }, [imageUrl]);

  const handleSave = useCallback(async (data: any) => {
    if (!queueId) {
      return false;
    }

    setError(null);
    try {
      setSavingAction(data?.action === 'reject' ? 'reject' : 'save');
      setSaving(true);
      const updateRequest = reportType === 'ticket_balance'
        ? buildFixOcrUpdateRequest(data)
        : buildNonTicketReportUpdateRequest(reportType, data, queueId);
      const storeIdToUse = apiData?.store_id || storeId;
      const response = await auditService.updateReceiptAuditStatusByReportType(
        queueId,
        reportType,
        updateRequest,
        storeIdToUse,
      );

      showToast.success(response.message || 'Audit updated successfully');

      if (reportType === 'ticket_balance' && response.latest_audit_id && storeIdToUse) {
        try {
          setAuditReportLoading(true);
          const report = await getAuditReportExpanded(storeIdToUse, response.latest_audit_id);
          setAuditReport(report);
          setAuditReportDialogOpen(true);
        } catch (reportError) {
          console.error('Failed to fetch audit report:', reportError);
          showToast.warning('Audit saved but failed to load report. You can view it later.');
          navigateToAuditList(response.message || 'Audit updated successfully');
        } finally {
          setAuditReportLoading(false);
        }
      } else {
        navigateToAuditList(response.message || 'Audit updated successfully');
      }
      return true;
    } catch (saveError: any) {
      const errorMessage = saveError?.message || 'Unknown error';
      showToast.error('Failed to save changes: ' + errorMessage);
      setError('Failed to save changes: ' + errorMessage);
      return false;
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [apiData?.store_id, navigateToAuditList, queueId, reportType, storeId]);

  const onSubmitClickHandler = useCallback(async (data: any) => {
    const convertedApiData = isTicketBalanceReport ? convertFormToApiData(data) : data;
    if (convertedApiData.action === 'reject') {
      setPendingSaveData(convertedApiData);
      setPendingInvalidGames([]);
      setPendingInvalidPacks([]);
      setValidatingGamesPacks(false);
      setSaveConfirmationOpen(false);
      setRejectNotesDialogOpen(true);
      return;
    }

    if (reportType === 'vending_sales' || reportType === 'vending_inventory' || reportType === 'vending_shift_report') {
      setPendingSaveData(null);
      setPendingInvalidGames([]);
      setPendingInvalidPacks([]);
      setValidatingGamesPacks(false);
      setSaveConfirmationOpen(false);
      await handleSave(convertedApiData);
      return;
    }

    setPendingSaveData(convertedApiData);
    setPendingInvalidGames([]);
    setPendingInvalidPacks([]);
    setValidatingGamesPacks(true);
    setSaveConfirmationOpen(true);

    if (!isTicketBalanceReport) {
      setValidatingGamesPacks(false);
      return;
    }

    const storeIdToUse = apiData?.store_id || storeId;

    if (storeIdToUse && data.ticket_details && data.ticket_details.length > 0) {
      try {
        const activeGamesAndPacksToUse = activeGamesAndPacks.length > 0
          ? activeGamesAndPacks
          : await authService.getActiveGamesAndPacks(storeIdToUse);

        if (activeGamesAndPacks.length === 0 && activeGamesAndPacksToUse.length > 0) {
          setActiveGamesAndPacks(activeGamesAndPacksToUse);
        }

        const validationState = validateFixOcrFormState(data, activeGamesAndPacksToUse);
        setPendingInvalidGames(validationState.invalidGames);
        setPendingInvalidPacks(validationState.invalidPacks);
      } catch (validationError) {
        console.error('Failed to validate against active games and packs:', validationError);
      } finally {
        setValidatingGamesPacks(false);
      }
      return;
    }

    setValidatingGamesPacks(false);
  }, [activeGamesAndPacks, apiData?.store_id, handleSave, isTicketBalanceReport, reportType, storeId]);

  const handleEditReceiptSuccess = useCallback(async (response: UploadReceiptResponse) => {
    try {
      if (response?.queue_id) {
        const params = new URLSearchParams(location.search);
        const storeIdToUse = storeId || '';
        if (storeIdToUse) {
          params.set(FIX_OCR_STORE_ID_PARAM, storeIdToUse);
        } else {
          params.delete(FIX_OCR_STORE_ID_PARAM);
        }
        params.delete(FIX_OCR_STORE_ID_PARAM_LEGACY);

        const nextSearch = params.toString();
        navigate({
          pathname: `/dashboard/audit/fix-ocr/${response.queue_id}`,
          search: nextSearch ? `?${nextSearch}` : '',
        });
        showToast.success('New receipt uploaded. Redirecting...');
        return;
      }
      showToast.warning('Upload succeeded but no queue ID returned');
    } catch (editReceiptError: any) {
      showToast.error(editReceiptError?.message || 'Failed to open new receipt');
    } finally {
      setEditReceiptDialogOpen(false);
    }
  }, [location.search, navigate, storeId]);

  const handleSwitchModes = useCallback((data: any, mode: 'form' | 'json') => {
    setApiData(data);

    if (!isTicketBalanceReport) {
      setFormData(commitPlainFormData(data));
      setEditMode(mode);
      return;
    }

    const validationState = getValidationState(data);
    setFormData(commitValidationState(validationState));
    setEditMode(mode);
  }, [commitPlainFormData, commitValidationState, getValidationState, isTicketBalanceReport]);

  const handleConfirmSave = useCallback(async () => {
    if (!pendingSaveData) {
      setSaveConfirmationOpen(false);
      return;
    }

    const didSave = await handleSave(pendingSaveData);
    if (!didSave) {
      return;
    }

    setSaveConfirmationOpen(false);
    setPendingSaveData(null);
    setPendingInvalidGames([]);
    setPendingInvalidPacks([]);
  }, [handleSave, pendingSaveData]);

  const handleConfirmRejectWithNote = useCallback(async (note: string) => {
    if (!pendingSaveData) {
      setRejectNotesDialogOpen(false);
      return;
    }

    setRejectNotesSaving(true);
    const didSave = await handleSave({ ...pendingSaveData, notes: note });
    setRejectNotesSaving(false);
    if (!didSave) {
      return;
    }

    setRejectNotesDialogOpen(false);
    setPendingSaveData(null);
  }, [handleSave, pendingSaveData]);

  const handleCloseAuditReport = useCallback(() => {
    setAuditReportDialogOpen(false);
    setAuditReport(null);
    navigateToAuditList('Audit saved successfully');
  }, [navigateToAuditList]);

  const handleRevertAudit = useCallback(async () => {
    if (!queueId) {
      showToast.error('Cannot revert: No queue ID available');
      return;
    }

    try {
      setRevertingAudit(true);
      const response = await auditService.revertToReady(queueId);
      showToast.success(response.message || 'Audit reverted to ready successfully');
      setAuditReportDialogOpen(false);
      setAuditReport(null);
      setAuditStatus('ready');
      setFormData((previousFormData: any) => (
        previousFormData
          ? {
              ...previousFormData,
              action: 'finalize',
            }
          : previousFormData
      ));
      if (formRef.current?.setValue) {
        formRef.current.setValue('action', 'finalize');
      }
      setApiData((previousApiData: any) => (
        previousApiData
          ? {
              ...previousApiData,
              status: 'ready',
            }
          : previousApiData
      ));
    } catch (revertError: any) {
      console.error('Error reverting to ready:', revertError);
      showToast.error(revertError?.message || 'Failed to revert audit to ready');
    } finally {
      setRevertingAudit(false);
    }
  }, [queueId]);

  const handleCancelSave = useCallback(() => {
    if (formRef.current?.setValue) {
      formRef.current.setValue('action', 'finalize');
    }
    setSaveConfirmationOpen(false);
    setRejectNotesDialogOpen(false);
    setPendingSaveData(null);
    setPendingInvalidGames([]);
    setPendingInvalidPacks([]);
  }, []);

  const handleEditReceiptClick = useCallback(() => {
    setEditReceiptDialogOpen(true);
  }, []);

  const handleEditReceiptClose = useCallback(() => {
    setEditReceiptDialogOpen(false);
  }, []);

  const handleRevertToReady = useCallback(async () => {
    if (!queueId) {
      return false;
    }

    try {
      setSavingAction('revert');
      setSaving(true);
      const response = await auditService.revertToReady(queueId);
      showToast.success(response.message || 'Audit reverted to ready successfully');
      navigateToAuditList();
      return true;
    } catch (revertError: any) {
      console.error('Error reverting to ready:', revertError);
      showToast.error(revertError?.message || 'Failed to revert audit to ready');
      return false;
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [navigateToAuditList, queueId]);

  return {
    queueId,
    storeId,
    storeIdForReceipt,
    storeName,
    reportType,
    vendingInventoryComparison,
    imageUrl,
    loading,
    saving,
    savingAction,
    error,
    formData,
    apiData,
    editMode,
    formKey,
    auditStatus,
    editReceiptDialogOpen,
    auditReportDialogOpen,
    auditReport,
    auditReportLoading,
    revertingAudit,
    saveConfirmationOpen,
    rejectNotesDialogOpen,
    rejectNotesSaving,
    pendingSaveData,
    pendingInvalidGames,
    pendingInvalidPacks,
    validatingGamesPacks,
    simulationLoading,
    simulationError,
    simulationResult,
    varianceByGameId,
    storeLocations,
    gamesData,
    hoveredRowIndex,
    gamesMapState,
    formRef,
    onSubmitClickHandler,
    handleJsonOnChange,
    handleFormDataUpdate,
    handleSwitchModes,
    handleEditReceiptClick,
    handleEditReceiptClose,
    handleEditReceiptSuccess,
    handleConfirmSave,
    handleConfirmRejectWithNote,
    handleCloseAuditReport,
    handleRevertAudit,
    handleCancelSave,
    handleRevertToReady,
    handleAuditBreadcrumbClick: () => navigateToAuditList(),
    onRowHover: setHoveredRowIndex,
    handleRefreshSimulation,
  };
};

export default useFixOcrPageLogic;
